import { Feature } from "common/types";
import { MakeGenerics, RouteDefinition, Routes } from "common/router/types";
import { makeRoutes, MakeRoutesOpts } from "common/router/utils/routes";

/** Page-level metadata stored in route.meta for analytics tracking. */
export type ClientRouteMeta = {
  pageName?: string | ((params: Record<string, string>) => string);
  pageType?: string;
  /** When true, suppress the page_view event for this route (e.g. redirect-only routes). */
  skipPageView?: boolean;
};

/**
 * Client-specific generics that give typed access to route.meta on matches.
 * Usage: `useMatches<ClientGenerics>()` → `match.route.meta?.pageName`
 */
export type ClientGenerics = MakeGenerics<{ RouteMeta: ClientRouteMeta }>;

/** Leaf route definition that requires `pageName` for analytics. */
export type ClientLeafRoute = RouteDefinition & {
  pageName: string | ((params: Record<string, string>) => string);
  requiresHd?: boolean;
  /** When true, suppress the page_view event for this route (e.g. redirect-only routes). */
  skipPageView?: boolean;
};

/** Parent module route definition that requires `pageType` for analytics. */
export type ClientModuleRoute = {
  path: string;
  children: Routes;
  pageType: string;
  requiresHd?: boolean;
  featureFlag?: Feature;
};

/**
 * Wraps `makeRoutes` for client storefront leaf routes.
 * Moves `pageName` into `route.meta` so it's accessible via typed `useMatches<ClientGenerics>()`.
 * Enforces that every leaf route defines a `pageName`.
 */
export const makeClientRoutes = (opts: MakeRoutesOpts, routes: ClientLeafRoute[]): Routes => {
  return makeRoutes(
    opts,
    routes.map(({ pageName, skipPageView, ...route }) => ({
      ...route,
      meta: { pageName, ...(skipPageView && { skipPageView }) },
    })),
  );
};
